import { useSyncExternalStore } from 'react'

import {
  ensureChatConfig,
  getChatConfig,
  subscribeChatConfig,
  type ChatConfig,
} from '@/lib/chatConfig'
import { isDecagonEnabled, openDecagon } from '@/lib/decagon'
import { openChat as openZendeskChat } from '@/lib/zendesk'

// 'none' = no chat (every chat flag off, or config still loading).
export type ChatProvider = 'decagon' | 'zendesk' | 'none'

// The A/B experiment is owned by Optimizely (Web), which writes a per-visitor
// `chat_provider` cookie. Only honored while chat_ab_test is on (same as mono),
// so a stale cookie never overrides steady state.
function readCookieArm(): 'decagon' | 'zendesk' | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(/(?:^|;\s*)chat_provider=(decagon|zendesk)\b/)
  return (match?.[1] as 'decagon' | 'zendesk') ?? null
}

// Steady-state precedence, mirroring mono's chat/_web: Decagon (flag on AND slug
// configured) > Zendesk (flag on) > none.
function steadyProvider(cfg: ChatConfig): ChatProvider {
  if (cfg.decagon && isDecagonEnabled()) return 'decagon'
  if (cfg.zendesk) return 'zendesk'
  return 'none'
}

/**
 * Resolve the chat provider from the mono-owned config (single source across
 * surfaces). During the A/B experiment (chat_ab_test) Optimizely's per-visitor
 * `chat_provider` cookie decides; otherwise the per-brand flags do. 'none' while
 * the config is still loading and when every chat flag is off.
 */
export function getChatProvider(): ChatProvider {
  const cfg = getChatConfig()
  if (!cfg) return 'none'
  if (cfg.chat_ab_test) return readCookieArm() ?? steadyProvider(cfg)
  return steadyProvider(cfg)
}

/** Open whichever chat widget is active (used by header "chat" buttons). */
export function openChat(): void {
  const provider = getChatProvider()
  if (provider === 'decagon') {
    openDecagon()
  } else if (provider === 'zendesk') {
    openZendeskChat()
  }
}

function subscribe(callback: () => void): () => void {
  ensureChatConfig()
  return subscribeChatConfig(callback)
}

/**
 * Resolves which chat widget to mount. SSR renders 'none'; on the client it
 * kicks the config fetch and re-resolves once it (and the Optimizely cookie)
 * are available — no hydration mismatch, no setState-in-effect.
 */
export function useResolvedChatProvider(): ChatProvider {
  return useSyncExternalStore(subscribe, getChatProvider, () => 'none')
}
